import { useEffect } from 'react'
import { FitpetIcon } from '@fitpet/ui/components'
import Link from 'next/link'
import styled, { useTheme } from 'styled-components'
import { Spinner } from '@/components/common/etc'
import FlatIcon from '@/components/common/icons/FlatIcon'
import { Typo, TypoVariant } from '@/components/common/typography'
import BasicCouponCard from '@/components/domains/coupon/BasicCouponCard'
import { MypageCouponsTopNav } from '@/components/domains/coupon/mypage-coupons-top-nav'
import { LayoutSelectableTabs } from '@/components/domains/layouts'
import { IconNameEnum } from '@/constants/icon-name.enum'
import { IconSizeEnum } from '@/constants/icon-size.enum'
import { FIXED_HEIGHT } from '@/constants/legacy/constLayout'
import ROUTES from '@/constants/legacy/constRoutes'
import { USER_COUPON_NOTICES } from '@/constants/legacy/constText'
import { couponItems, useCouponList } from '@/containers/coupon/coupon-list.hook'
import { useCouponEventTracker } from '@/containers/event-tracker/coupon.event-tracker'
import { useCustomRouter } from '@/containers/hooks'
import { UserContainer } from '@/containers/users/UserContainer'
import {
  checkExcludedCoupon,
  CouponGroupType,
  ExcludedCouponTypes,
  IncludedCouponTypes,
  makeCouponDisplayData,
  makeCouponIncludeTargetString,
} from '@/utils/coupon.util'

const PageMyPageCouponList = () => {
  const { color } = useTheme()
  const { push, isReady } = useCustomRouter()
  const { me, meIsLoading: isMeLoading } = UserContainer.useContainer()
  const {
    couponListData,
    setCouponStatus,
    couponStatus,
    isLoadingCouponList,
    totalCount,
    couponList,
    isBannerError,
    bannerImageData,
    isAvailableCouponStatus,
    getCouponStatusById,
  } = useCouponList()

  const {
    trackViewCouponPageEvent,
    trackClickCouponPageTabsEvent,
    trackClickCouponPageRegisterEvent,
    trackViewCouponPageBannerEvent,
    trackClickCouponPageBannerEvent,
  } = useCouponEventTracker()

  useEffect(() => {
    if (isReady && !isLoadingCouponList && bannerImageData) {
      trackViewCouponPageBannerEvent({
        totalCouponCount: totalCount,
        couponListData,
        bannerNameSlug: bannerImageData.slug.value,
        bannerClickUrl: bannerImageData.link,
      })
      trackViewCouponPageEvent({
        totalCouponCount: totalCount,
        couponListData,
      })
    }
  }, [isReady, isLoadingCouponList])

  const handleClickCouponStatusTab = (id: string) => {
    const selectedCouponStatus = getCouponStatusById(id)
    trackClickCouponPageTabsEvent({
      couponStatus: selectedCouponStatus,
      totalCouponCount: totalCount,
      couponListData,
    })
    setCouponStatus(selectedCouponStatus)
  }

  const handleClickCouponBanner = () => {
    if (bannerImageData) {
      trackClickCouponPageBannerEvent({
        totalCouponCount: totalCount,
        couponListData,
        bannerNameSlug: bannerImageData.slug.value,
        bannerClickUrl: bannerImageData.link,
      })

      push({
        pathname: new URL(bannerImageData.link).pathname.replace('/mall', ''),
        query: { location: 'couponpage' },
      })
    }
  }

  const handleClickCouponAddLink = () => {
    trackClickCouponPageRegisterEvent({
      totalCouponCount: totalCount,
      couponListData,
    })
  }

  const isShowNotice = !!totalCount && totalCount > 0
  const isShowCouponBanner = !isBannerError && !!bannerImageData?.imageUrl

  useEffect(() => {
    if (isReady && !isMeLoading && !me) {
      push(ROUTES.ACCOUNTS.LOGIN + '?return_url=' + window.location.pathname)
    }
  }, [isReady, isMeLoading, me])

  return (
    <>
      <MypageCouponsTopNav />
      <StyledContainer className="coupon-page">
        <LayoutSelectableTabs items={couponItems} onChange={(id) => handleClickCouponStatusTab(id as string)} />
        <div className="coupon-page__wrapper" key={couponStatus}>
          {isAvailableCouponStatus && (
            <>
              <div className="coupon-page__add" onClick={handleClickCouponAddLink}>
                <Link href={ROUTES.MYPAGE.COUPONS_ADD} passHref>
                  <div className="coupon-page__add-content">
                    <FitpetIcon icon="icAdd" color={color.blue500} size={16} />
                    <Typo variant={TypoVariant.Body1Bold} color={color.blue500}>
                      쿠폰 등록하기
                    </Typo>
                  </div>
                </Link>
              </div>
              <div className="coupon-page__divider" />
            </>
          )}
          <div>
            <div className="coupon-page__total">
              <Typo variant={TypoVariant.Body3Regular} color={color.gray500}>
                총 {totalCount}장
              </Typo>
            </div>
          </div>
          {isShowCouponBanner && (
            <div className="coupon-page__banner" onClick={handleClickCouponBanner}>
              <img
                data-cy="coupon-banner"
                className="coupon-page__banner-image"
                src={bannerImageData.imageUrl}
                alt={bannerImageData.slug.value}
              />
            </div>
          )}
          <ul className="coupon-page__list">
            {couponList.map(
              ({
                id,
                couponGroup,
                expiredAt,
                usedStatus,
                isMembershipCoupon = false,
                canConjunctUse = false,
                name = '',
                includedBrands,
                includedCategories,
                includedProducts,
                excludedBrands,
                excludedCategories,
                excludedProducts,
              }) => {
                const { title, amount, datePeriod } = makeCouponDisplayData({
                  ...couponGroup,
                  expiredAt,
                  usedStatus,
                } as unknown as CouponGroupType)
                const couponCoverageContent = makeCouponIncludeTargetString({
                  includedBrands,
                  includedCategories,
                  includedProducts,
                } as IncludedCouponTypes)
                const isExcludeCouponCoverage = checkExcludedCoupon({
                  excludedBrands,
                  excludedCategories,
                  excludedProducts,
                } as ExcludedCouponTypes)
                return (
                  <li key={id} className="coupon-page__item">
                    <BasicCouponCard
                      key={id}
                      title={title}
                      isMembership={isMembershipCoupon}
                      isConjunctUse={canConjunctUse}
                      name={name}
                      amount={amount}
                      datePeriod={datePeriod}
                      couponCoverageContent={couponCoverageContent}
                      isExcludeCouponCoverage={isExcludeCouponCoverage}
                      disabled={!isAvailableCouponStatus}
                    />
                  </li>
                )
              }
            )}
          </ul>
          {isLoadingCouponList && (
            <div className="coupon-page__loading">
              <Spinner />
            </div>
          )}
        </div>
        {isShowNotice && (
          <div className="coupon-page__notice">
            <div className="coupon-page__notice-title">
              <FlatIcon type={IconNameEnum.IcInfo} color={color.gray400} size={IconSizeEnum.Size20} />
              <Typo variant={TypoVariant.Body3Bold} color={color.gray400}>
                유의사항
              </Typo>
            </div>
            <ul className="coupon-page__notice-list">
              {USER_COUPON_NOTICES.map((notice, index) => (
                <li className="coupon-page__notice-item" key={index}>
                  <Typo variant={TypoVariant.Body3Regular} color={color.gray400}>
                    {notice}
                  </Typo>
                </li>
              ))}
            </ul>
          </div>
        )}
      </StyledContainer>
    </>
  )
}

const StyledContainer = styled.div`
  display: flex;
  flex-direction: column;
  min-height: calc(100vh - ${FIXED_HEIGHT.HEADER / 10}rem);
  height: auto;
  background-color: ${({
    theme: {
      color: { gray50 },
    },
  }) => gray50};
  .coupon-page {
    &__wrapper {
      display: flex;
      flex-direction: column;
      flex: 1;
      padding: 2.4rem 1.6rem;
      background-color: ${({
        theme: {
          color: { grayWhite },
        },
      }) => grayWhite};
    }
    &__add {
      padding: 1.3rem 1.6rem 1.3rem 1.4rem;
      border: 1px solid
        ${({
          theme: {
            color: { blue500 },
          },
        }) => blue500};
      border-radius: 0.8rem;
      margin-bottom: 2.4rem;
      cursor: pointer;
    }
    &__add-content {
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 0.4rem;
    }
    &__divider {
      background-color: ${({
        theme: {
          color: { gray70 },
        },
      }) => gray70};
      height: 0.8rem;
      margin: 0 -1.6rem 2.4rem;
    }
    &__total {
      margin: 0 0 1.2rem 0;
    }
    &__banner {
      cursor: pointer;
      position: relative;
      margin-bottom: 1.2rem;
    }
    &__banner-image {
      width: 100%;
    }
    &__item {
      &:not(:last-child) {
        margin-bottom: 1.2rem;
      }
    }
    &__loading {
      display: flex;
      align-items: center;
      justify-content: center;
      flex: 1;
    }
    &__notice {
      padding: 2.4rem 1.6rem 4.8rem;
    }
    &__notice-title {
      display: flex;
      gap: 0.4rem;
      margin-bottom: 1.2rem;
    }
    &__notice-list {
    }
    &__notice-item {
      position: relative;
      margin-bottom: 0.8rem;
      padding-left: 1.1rem;
      color: ${({
        theme: {
          color: { gray400 },
        },
      }) => gray400};
      :not(:last-child) {
        margin-bottom: 0.8rem;
      }
      :last-child {
        margin-bottom: 0;
      }
      :before {
        content: '•';
        position: absolute;
        left: 0;
        top: 0;
      }
    }
  }
`

export default PageMyPageCouponList
